import Head from 'next/head';
import Header from '../../components/Header';
import Hero from '../../components/Hero';
import dynamic from 'next/dynamic';
import services from '../../data/services/dynamic_services';
import geoCoverage from '../../data/geoCoverage';
import { generateWebPageSchema } from '../../utils/generateWebPageSchema';
import Link from 'next/link';

const SocialProof = dynamic(() => import('../../components/SocialProof'), { ssr: false });
const ContactUs = dynamic(() => import('../../components/ContactUs'), { ssr: false });
const Footer = dynamic(() => import('../../components/Footer'), { ssr: false });


export async function getStaticPaths() {
  // generate all service slugs
  const paths = services.map(service => ({
    params: { slug: service.link.replace('/services/', '') },
  }));
  return { paths, fallback: 'blocking' }; // fallback true if you want on-demand generation
}

export async function getStaticProps({ params }) {
  const matchedService = services.find(
    service => service.link.replace('/services/', '') === params.slug
  );

  return {
    props: {
      matchedService: matchedService || null,
      cities: geoCoverage.california,
    },
  };
}

const ServiceCityZipPage = ({ matchedService, cities }) => {
  const base = 'https://modernbuildremodel.com';

  if (!matchedService) {
    return (
      <>
        <Head>
          <title>Service Not Found | Modern Build</title>
        </Head>
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <h1 className="text-3xl font-semibold mb-4">Service Not Found</h1>
          <p className="text-gray-600">We couldn't find any coverage areas for this service.</p>
        </div>
        <Footer />
      </>
    );
  }

  const service = {
      title: `${matchedService.title} Coverage`,
      image: matchedService.imageSrc,
      description:
        `Discover Greater Los Angeles cities and ZIP codes where we provide ${matchedService.title.toLowerCase()}. Expert service, fully insured, fast turnaround, free estimates, 5 star rating`,
      link: matchedService.link.replace('/services', ''),
    };

  const pageUrl = `${base}/service-areas${service.link}`;
  const title = `${service.title} | Modern Build`;
  const description = service.description;

  // Breadcrumb JSON-LD
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": base },
      { "@type": "ListItem", "position": 2, "name": "Service Areas", "item": `${base}/service-areas` },
      { "@type": "ListItem", "position": 3, "name": matchedService.title, "item": pageUrl }
    ]
  };

  // Service JSON-LD
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${pageUrl}#service`,
    "mainEntityOfPage": { "@id": `${pageUrl}#webpage` },
    "name": `${service.title} in Los Angeles, CA`,
    "serviceType": service.title.replace("/", "and"),
    "description": service.description,
    "url": pageUrl,
    "image": `${base}${service.image}`,
    "provider": { "@id": `${base}#localbusiness` },
    "areaServed": [
      { "@type": "AdministrativeArea", "name": "Los Angeles County, CA" },
      { "@type": "AdministrativeArea", "name": "Orange County, CA" },
      { "@type": "AdministrativeArea", "name": "Ventura County, CA" }
    ],
    "offers": [
      {
        "@type": "Offer",
        "url": pageUrl,
        "priceCurrency": "USD",
        "price": "0",
        "name": "Free Estimate",
        "description": "Get a free, no-obligation estimate for your project",
        "availability": "https://schema.org/InStock",
      },
      {
        "@type": "Offer",
        "url": `${pageUrl}#financing`,
        "name": "Financing Available",
        "description": "Flexible financing options with $0 down",
        "availability": "https://schema.org/InStock"
      }
    ]
  };

  const safeServiceTitle = matchedService?.title || "Service Areas";
  
  return (
    <>
    <Head>
      <title>{`${safeServiceTitle} Coverage | Modern Build`}</title>
      <meta name="robots" content="index, follow" />
      <meta name="title" content={`${safeServiceTitle} Coverage | Modern Build`} />
      <meta name="description" content={`Discover Greater Los Angeles cities and ZIP codes where we provide ${safeServiceTitle.toLowerCase()}. Expert service, fully insured, fast turnaround, free estimates, 5 star rating`} />
      <meta name="keywords" content={`${safeServiceTitle}, service coverage, California remodeling areas, ZIP codes`} />
      <meta name="author" content="Modern Build Home Remodeling" />
      <meta name="robots" content="index,follow" />

      <link rel="canonical" href={pageUrl} />

      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={`${safeServiceTitle} Coverage | Modern Build`} />
      <meta property="og:description" content={`Discover Greater Los Angeles cities and ZIP codes where we provide ${safeServiceTitle.toLowerCase()}. Expert service, fully insured, fast turnaround, free estimates, 5 star rating`} />
      <meta property="og:image" content={`https://modernbuildremodel.com${matchedService.imageSrc}`} />
      <meta property="og:url" content={`https://modernbuildremodel.com/service-areas${matchedService.link.replace('/services', '')}`} />
      <meta property="og:site_name" content="Modern Build Home Remodeling" />
      <meta property="og:type" content="website" />

      {/* Twitter Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={`${safeServiceTitle} Coverage | Modern Build`} />
      <meta name="twitter:description" content={`Discover Greater Los Angeles cities and ZIP codes where we provide ${safeServiceTitle.toLowerCase()}. Expert service, fully insured, fast turnaround, free estimates, 5 star rating`} />
      <meta name="twitter:image" content={`https://modernbuildremodel.com${matchedService.imageSrc}`} />
      <meta name="twitter:site" content="@modernbuildhome" />

      {/* JSON-LD: WebPage */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify(generateWebPageSchema({ title, description, slug: pageUrl }))
        }} />

        {/* JSON-LD: Breadcrumb */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

        {/* JSON-LD: Service */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }} />
      </Head>


      <Header />
      <Hero
        page="page"
        title={[`${safeServiceTitle}`, 'ZIP Codes & Cities']}
        subtitle={`Explore all cities and ZIP codes in California where we offer ${safeServiceTitle.toLowerCase()} services.`}
        button="Get a Free Estimate"
        backgroundImage={matchedService.imageSrc || '/hero/best-home-remodling-contractor-in-los-angeles.webp'}
        hasContentH1={true}
      />
<div className="max-w-7xl mx-auto px-4 py-16">
  <h1 className="text-4xl font-bold mb-12 text-center">
    {safeServiceTitle} Service Coverage in California
  </h1>

  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
    {Object.entries(cities).map(([city, zipCodes]) => (
      <div key={city} className="bg-white shadow-md rounded-xl p-6 border border-gray-200">
        <h2 className="text-xl font-semibold mb-4 capitalize text-primary border-b border-gray-100 pb-2">
          {city.replace(/-/g, ' ')}
        </h2>
        <ul className="flex flex-wrap gap-2">
          {zipCodes.map(zip => (
            <li key={zip}>
            <Link
                href={`/${matchedService.link.replace('/services/', '')}/california/${city}/${zip}`}
                className="inline-block px-3 py-1.5 text-sm bg-primary text-white rounded-md hover:bg-black transition-all duration-300"
              >
                {zip}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    ))}
  </div>
</div>

      <SocialProof />
      <ContactUs />
      <Footer />
    </>
  );
};

export default ServiceCityZipPage;